/** @jsxImportSource preact */
import { useState, useEffect } from "preact/hooks";

type SiteInfoResult = {
  ip_address?: string;
  redirect_url?: string;
  cms?: {
    status?: string;
    cms?: string;
    confidence?: string;
    version?: string | null;
  };
  cdn?: string;
  running_on?: string;
  powered_by?: string;
};

type ScanResultItem = {
  status?: string;
  error?: string;
};

type ScanResult = {
  [service: string]: ScanResultItem | string | null;
};

type HardeningResult = {
  // ───── SPF Detection ─────
  spf_record_count?: number;
  spf_records?: string[];

  // ───── Security Headers ─────
  security_headers?: {
    missing_headers?: {
      [headerName: string]: {
        status: "missing" | "missing_or_incorrect";
        recommended?: string;       // e.g. "nosniff"
        recommendation?: string;    // e.g. CSP string suggestion
        current?: string;           // if header present but misconfigured
      };
    };
  };

  // ───── Cookie & Port Checks ─────
  secure_cookies?: {
    insecure_cookies?: {
      [cookieName: string]: string[];
    };
    secure: boolean;
  };
  open_ports?: {
    open_ports: {
      port: number;
      service: string;
    }[];
    hardened: boolean;
  };

  // ───── Server & TRACE & XSS ─────
  server_banner?: {
    exposed: boolean;
    value?: string;
  };
  trace_method?: {
    trace_enabled: boolean;
  };
  xss_protection?: {
    status: "enabled" | "missing_or_incorrect";
    current?: string;
  };
};


interface MonitorData {
  uptime: {
    status: number | string;
    response_time: number;
  };
  ssl: {
    valid: boolean;
    days_remaining: number;
    message: string;
  };
  defacement: {
    detected: boolean;
    message: string;
  };
  spam: {
    detected: boolean;
    message: string;
    sql_injection: boolean;
    hidden_links: string[];
    keywords: string[];
  };
}

type ActualMonitor = {
  color: string;
  display_text: string;
  icon: string;
  status: string;
};

export default function ScanForm() {
  // ──────────────────────────── State Declarations ────────────────────────────
  const [url, setUrl] = useState("");
  const [result, setResult] = useState<ScanResult | null>(null);
  const [siteInfo, setSiteInfo] = useState<SiteInfoResult | null>(null);
  const [scannedUrl, setScannedUrl] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // “Slower” API sections:
  const [firewallResult, setFirewallResult] = useState<{ firewall_detected: boolean; details: string[] } | null>(null);
  const [monitorData, setMonitorData] = useState<MonitorData | null>(null);
  const [hardeningResult, setHardeningResult] = useState<HardeningResult | null>(null);
  const [monitoringCheck, setMonitoringCheck] = useState<ActualMonitor | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const formatUSPhone = (value: string) => {
    // strip all non-digits
    const digits = value.replace(/\D/g, "").slice(0,10);
    const part1 = digits.slice(0,3);
    const part2 = digits.slice(3,6);
    const part3 = digits.slice(6,10);
    if (part3) return `(${part1}) ${part2}-${part3}`;
    if (part2) return `(${part1}) ${part2}`;
    if (part1) return `(${part1}`;
    return digits;
  };


  useEffect(() => {
    if (!loading) return;
    const timer = setTimeout(() => {
      if (monitorData === null) {
        setError(
          "Malware scanner may be temporarily unavailable"
        );
        setMonitorData({
          uptime: { status: "Unavailable", response_time: 0 },
          ssl: { valid: false, days_remaining: 0, message: "Unavailable" },
          defacement: { detected: false, message: "Unavailable" },
          spam: { detected: false, sql_injection: false, hidden_links: [], keywords: [], message: "Unavailable" },
        });
      }
    }, 29000);
    return () => clearTimeout(timer);
  }, [loading]);

  // ─────────────────────────── hasMalwareIssue Helper ───────────────────────────
  const hasMalwareIssue = () => {
    if (!monitorData) return false;
    const { uptime, spam, defacement, ssl } = monitorData;
    return (
      uptime.status !== 200 ||
      spam.detected === true ||
      defacement.detected === true ||
      ssl.valid === false ||
      spam.sql_injection === true
    );
  };

  // ───────────────────────── computeRiskPercent Helper ─────────────────────────
  const computeRiskPercent = () => {
    let score = 0;

    // BLACKLIST risk:
    if (result) {
      const blacklistedCount = Object.values(result).filter((entry) => {
        if (entry && typeof entry === "object" && "status" in entry) {
          return ((entry as ScanResultItem).status || "")
            .toLowerCase()
            .includes("blacklisted");
        }
        return false;
      }).length;

      if (blacklistedCount > 4) {
        score += 25;
      } else if (blacklistedCount >= 1) {
        score += 10;
      }
    }

    // MALWARE risk (5 points each):
    if (monitorData) {
      if (monitorData.ssl?.valid === false) score += 5;
      if (monitorData.spam?.detected === true) score += 5;
      if (monitorData.defacement?.detected === true) score += 5;
      if (monitorData.spam?.sql_injection === true) score += 5;
    }

    // FIREWALL risk:
    if (firewallResult && firewallResult.firewall_detected === false) {
      score += 10;
    }

    // WEBSITE MONITORING risk:
    if (monitoringCheck && monitoringCheck.status !== "detected") {
      score += 3;
    }

    // HARDENING & OPEN PORTS:
    if (hardeningResult) {
      const openPorts = hardeningResult.open_ports?.open_ports || [];
      if (openPorts.length > 0) score += 3;

      const missingHeaders = hardeningResult.security_headers?.missing_headers || {};
      const hasMissingHeaders = Object.keys(missingHeaders).length > 0;
      const hasTraceEnabled = hardeningResult.trace_method?.trace_enabled === true;
      const hasBannerExposed = hardeningResult.server_banner?.exposed === true;
      if (hasMissingHeaders || hasTraceEnabled || hasBannerExposed) score += 5;
    }

    if (score > 100) score = 100;
    if (score < 0) score = 0;
    return score;
  };

  // ───────────────────────────── handleSubmit ─────────────────────────────
  const handleSubmit = async (e: Event) => {
    e.preventDefault();

    // ─── 1) Client-side URL validation ───
    const urlValue = url.trim();
    // Basic regex allows optional “http(s)://”, at least one “label.” and a TLD, optional path.
    const basicUrlRegex = /^(https?:\/\/)?([^\s.]+\.)+[^\s]{2,}(\/.*)?$/i;
    if (!basicUrlRegex.test(urlValue)) {
      setError("Invalid URL format. Please enter a valid domain (e.g. example.com)");
      return;
    }

    // ─── 2) Clear previous state and show “Scanning…” ───
    setLoading(true);
    setResult(null);
    setSiteInfo(null);
    setFirewallResult(null);
    setMonitorData(null);
    setHardeningResult(null);
    setMonitoringCheck(null);
    setError(null);

    try {
      const encoded = encodeURIComponent(urlValue);

      // 1) BLACKLIST SCAN
      const blRes = await fetch(`/api/scan/blacklists/${encoded}`);
      if (!blRes.ok) throw new Error("Blacklist scanner is currently offline");
      const blData = await blRes.json();
      setResult(blData);

      // 2) SITE INFO
      const siRes = await fetch(`/api/scan/siteinfo/${encoded}`);
      if (!siRes.ok) throw new Error("Site information finder is currently offline");
      const siData = await siRes.json();
      setSiteInfo(siData);

      // 3) FIREWALL DETECTION
      const fwRes = await fetch(`/api/scan/firewall/${encoded}`);
      if (!fwRes.ok) throw new Error("Firewall detector is currently offline");
      const fwData = await fwRes.json();
      setFirewallResult(fwData);


      const domainOnly = urlValue.replace(/^https?:\/\//, "").split("/")[0];

      // **Hardening improvements** now takes two query params: url & domain
      const hardeningFetch = fetch(
        `/api/scan/hardening?` +
          `url=${encodeURIComponent("http://" + domainOnly)}` +
          `&domain=${encodeURIComponent(domainOnly)}`
      )
        .then(res => {
          if (!res.ok) throw new Error("Error loading Hardening Improvements");
          return res.json();
        });

      const monitoringFetch = fetch(`/api/check/monitoring/${encoded}`).then(res => { if (!res.ok) throw new Error("Website Monitoring detector is currently offline"); return res.json(); });

      const statusFetch = fetch(`/api/status/${encoded}`).then(res => { if (!res.ok) throw new Error("Malware scanner is currently offline"); return res.json(); });

      const [hrData, mcJson, mdJson] = await Promise.all([hardeningFetch, monitoringFetch, statusFetch]);
      setHardeningResult(hrData);
      setMonitoringCheck(mcJson.monitoring);
      setMonitorData(mdJson);


      // Finally, record the scanned URL and clear the input field
      setScannedUrl(urlValue);
      setUrl("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "An unknown error occurred");
    } finally {
      setLoading(false);
    }
  };

  // ──────────────────────────────── JSX Output ───────────────────────────────
  return (
    <>
      {/* ───────────── Logo + Header ───────────── */}
      <header class="sticky top-0 z-50 bg-[#000000] border-b border-gray-700">
        <div class="max-w-[85rem] mx-auto flex items-center py-4 px-4 sm:px-0 ">
          <img
            src="/images/logo2.svg"
            alt="Your Company Logo"
            class="h-9 w-auto"
          />
          <span class=" text-white text-3xl font-segoe-ui">
            eroxion
          </span>
        </div>
      </header>

      <div class="bg-[#000000] min-h-screen py-10 px-4">
        {/* ───── Header + Input Form ───── */}
        <div class="max-w-[85rem] mx-auto text-center mb-10 mt-5">
          <h1 class="text-5xl text-white mb-2 font-bold">Website Security Checker</h1>
          <p class="text-[#e5e7eb] text-lg">Scan your website for malware, spam, blacklists, and more.</p>

          <form onSubmit={handleSubmit} class="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
            <input
              type="text"
              value={url}
              onInput={(e) => setUrl((e.target as HTMLInputElement).value)}
              placeholder={result ? "Check another URL" : "Enter a website URL (e.g., example.com)"}
              class="flex-grow border border-gray-300 p-3 rounded-md text-base shadow-sm"
            />
            {/* <button
              type="submit"
              class="bg-[#004e89] text-white px-6 py-3 rounded-md font-semibold shadow hover:bg-[#c2001a] transition disabled:opacity-50"
              disabled={loading}
            >
              {loading ? "Scanning" : "Scan"}
            </button> */}
            <button
              type="submit"
              disabled={loading}
              class={`text-white px-6 py-3 rounded-md font-semibold shadow transition disabled:opacity-50
                ${
                  loading
                    ? "bg-[#c2001a] hover:bg-[#c2001a]"
                    : "bg-[#004e89] hover:bg-[#c2001a]"
                }`}
            >
              {loading ? "Scanning" : "Scan"}
            </button>

          </form>

          {/* ───── Error Message Box ───── */}
          {error && (
            <div class="mt-6 bg-[#1a1a1a] border border-[#ff002b] text-white rounded p-4 max-w-xl mx-auto shadow">
              <div class="flex items-center gap-2">
                <img src="/images/warn_red2.png" alt="Warning" class="w-5 h-5" />
                <span class="font-semibold text-[#ff002b]">Something went wrong while scanning.</span>
              </div>
              <p class="mt-2 ml-6 text-gray-300 text-left text-sm">
                {error}. Please try again later or scan a different site.
              </p>
            </div>
          )}

          {/* ───── Placeholder Before First Scan ───── */}
          {!result && !loading && !error && (
            <div class="text-sm text-gray-500 mt-10">
              <p>Enter a website URL above to scan for malware, spam, blacklists, and firewall info.</p>
              <p class="mt-2">Uses trusted sources like VirusTotal, PhishTank, OpenPhish, etc.</p>
            </div>
          )}
        </div>

        {/* ───── Back Arrow + Scanned URL ───── */}
        {result && (
          <div class="max-w-[85rem] mx-auto mb-6">
            <div class="flex items-center gap-2 text-lg font-semibold text-white mb-4">
              <span
                class="text-3xl cursor-pointer mr-2"
                onClick={() => {
                  setResult(null);
                  setSiteInfo(null);
                  setFirewallResult(null);
                  setMonitorData(null);
                  setHardeningResult(null);
                  setMonitoringCheck(null);
                  setError(null);
                }}
              >
                ←
              </span>
              <span>{scannedUrl}</span>
            </div>
          </div>
        )}

        {/* ───────── Top-Level Badges: Malware + Blacklist ───────── */}
        {result && (
          <div class="max-w-[85rem] mx-auto bg-[#0f172a] rounded-xl shadow-md p-6 space-y-4 mt-10">
            <div class="flex flex-col sm:flex-row gap-4 max-w-[85rem] mx-auto">
              {/* 🦠 Malware Badge */}
              <div
                class={`flex border-2 ${
                  monitorData === null
                    ? "border-gray-500"
                    : hasMalwareIssue()
                      ? "border-[#ff002b]"
                      : "border-green-400"
                } bg-[#0f172a] text-white px-4 py-2 rounded shadow w-full sm:w-1/2`}
              >
                <div class="flex flex-col justify-center mr-3">
                  {monitorData === null ? (
                    <svg
                      class="animate-spin h-6 w-6 text-yellow-200"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      />
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8z"
                      />
                    </svg>
                  ) : hasMalwareIssue() ? (
                    <img src="/images/warn_red2.png" alt="⚠️" class="h-6 w-6 inline-block" />
                  ) : (
                    <img src="/images/check_new.png" alt="✅" class="h-6 w-6 inline-block" />
                  )}
                </div>

                <div class="flex flex-col">
                  <h3 class="font-bold text-base">
                    {monitorData === null
                      ? "Malware Detection"
                      : hasMalwareIssue()
                        ? "Potential Issues Detected"
                        : "No Malware Found"}
                  </h3>

                  {monitorData === null ? (
                    <p class="text-sm text-yellow-200 animate-pulse">
                      Scanning...
                    </p>
                  ) : (
                    <p class="text-sm">
                      {hasMalwareIssue()
                        ? "One or more checks flagged an issue"
                        : "Our scanner didn’t detect any malware"}
                    </p>
                  )}
                </div>
              </div>

              {/* 🔎 Blacklist Badge */}
              {(() => {
                const blacklistedCount = Object.values(result).filter((entry) => {
                  if (entry && typeof entry === "object" && "status" in entry) {
                    return ((entry as ScanResultItem).status || "").toLowerCase() === "blacklisted";
                  }
                  return false;
                }).length;
                const totalServices = Object.keys(result).length;
                const flagged = blacklistedCount > 0;
                const borderClass = flagged ? "border-[#ff002b]" : "border-green-400";
                const icon = (
                  <img
                    src={flagged ? "/images/warn_red2.png" : "/images/check_new.png"}
                    alt={flagged ? "⚠️" : "✅"}
                    className="w-6 h-6 inline-block"
                  />
                );
                const text = flagged
                  ? `Site is Blacklisted by ${blacklistedCount} of ${totalServices} sources`
                  : "Site is not Blacklisted";

                return (
                  <div
                    class={`flex items-center border-2 ${borderClass} bg-[#0f172a] text-white px-4 py-2 rounded shadow w-full sm:w-1/2`}
                  >
                    <span class="text-2xl mr-3">{icon}</span>
                    <div>
                      <h3 class="font-bold">{text}</h3>
                      <p class="text-sm">{totalServices} blacklist sources checked</p>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* ───────── Site Info (three equal columns) ───────── */}
            {siteInfo && (
              <div class="max-w-[85rem] mx-auto border border-gray-300 bg-[#0f172a] rounded-md px-6 py-5 mb-6">
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 text-white">
                  {/* Column 1: Redirect */}
                  {/* <div class="space-y-1 overflow-hidden">
                    <div class="flex items-center gap-2">
                      <span class="font-semibold">Redirects to:</span>
                      <a
                        href={siteInfo.redirect_url || `https://${scannedUrl}`}
                        class="text-blue-400 underline break-words"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {siteInfo.redirect_url || `https://${scannedUrl}`}
                      </a>
                    </div>
                    <p>
                      <strong>IP address:</strong> {siteInfo.ip_address || "Unknown"}
                    </p>
                  </div> */}

                  {/* <div class="space-y-1 overflow-hidden">
                    <div class="flex items-center gap-2">
                      <span class="font-semibold">Redirects to:</span>

                      {(() => {
                        const rawRedirect = siteInfo.redirect_url;

                        if (!rawRedirect) {
                          return (
                            <a
                              href={`https://${scannedUrl}`}
                              class="text-blue-400 underline break-words"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {`https://${scannedUrl}`}
                            </a>
                          );
                        }

                        const lower = rawRedirect.toLowerCase();

                        if (
                          lower.includes("exceeded 30 redirects") ||
                          lower.includes("redirect") && lower.includes("loop")
                        ) {
                          return (
                            <span class="text-[#ff002b] font-medium inline-flex items-center leading none">
                              <img src="/images/warn_red2.png" alt="⚠️" class="inline-block w-5 h-5 mr-1 flex-shrink-0 align-middle"/> 
                              <span className="align-middle"> Redirect loop detected</span>
                            </span>
                          );
                        }

                        if (
                          lower.includes("connection aborted") ||
                          lower.includes("connection closed")
                        ) {
                          return (
                          
                              
                              <span class="text-[#ff002b] font-medium">
                               <img src="/images/warn_red2.png" alt="⚠️" class="w-6 h-6"/>
                               Connection closed
                            </span>
                            
                            
                          );
                        }

                        return (
                          <a
                            href={rawRedirect.startsWith("http") ? rawRedirect : `https://${rawRedirect}`}
                            class="text-blue-400 underline break-words"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {rawRedirect.startsWith("http") ? rawRedirect : `https://${rawRedirect}`}
                          </a>
                        );
                      })()}
                    </div> */}
{/* 
                    <p>
                      <strong>IP address:</strong> {siteInfo.ip_address || "Unknown"}
                    </p>
                  </div> */}

                  <div class="space-y-1 overflow-hidden">
                    <div class="flex items-center gap-2">
                      <span class="font-semibold">Redirects to:</span>
                      {(() => {
                        const rawRedirect = siteInfo.redirect_url;
                        const normalized = rawRedirect?.toLowerCase() || "";

                        // Case A: redirect loop
                        if (
                          normalized.includes("exceeded 30 redirects") ||
                          (normalized.includes("redirect") && normalized.includes("loop"))
                        ) {
                          return (
                            <span class="text-[#ff002b] font-medium inline-flex items-center">
                              <img src="/images/warn_red2.png" alt="⚠️" class="inline-block w-5 h-5 mr-1" />
                              Redirect loop detected
                            </span>
                          );
                        }

                        // Case B: connection issues
                        if (
                          normalized.includes("connection aborted") ||
                          normalized.includes("connection closed") ||
                          normalized.includes("max retries exceeded") ||
                          normalized.includes("failed to resolve") ||
                          normalized.includes("name resolution error") ||
                          normalized.includes("httpconnectionpool") ||
                          normalized.includes("urlopenerror")
                        ) {
                          return (
                            <a
                              href={`https://${scannedUrl}`}
                              class="text-blue-400 underline break-words"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {`http://${scannedUrl}`}
                            </a>
                          );
                        }

                        // Case C: actual good URL
                        const urlToShow = rawRedirect?.startsWith("http")
                          ? rawRedirect
                          : `https://${rawRedirect}`;
                        return (
                          <a
                            href={urlToShow}
                            class="text-blue-400 underline break-words"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {urlToShow}
                          </a>
                        );
                      })()}
                    </div>

                    <p>
                      <strong>IP address:</strong>{" "}
                      {(() => {
                        const ip = siteInfo.ip_address?.toLowerCase();
                        if (
                          ip?.includes("errno -2") ||
                          ip?.includes("name or service not known") ||
                          ip?.includes("failed to resolve") ||
                          ip?.includes("unresolved")
                        ) {
                          return "Host not found";
                        }
                        return siteInfo.ip_address || "Unknown";
                      })()}
                    </p>
                  </div>

                  {/* Column 2: CDN / Running on */}
                  <div class="space-y-1 overflow-hidden">
                    <p>
                      <strong>CDN:</strong> {siteInfo.cdn || "Unknown"}
                    </p>
                    <p>
                      <strong>Running on:</strong> {siteInfo.running_on || "Unknown"}
                    </p>
                  </div>

                  {/* Column 3: CMS / Powered by */}
                  <div class="space-y-1 overflow-hidden">
                   
                    <p>
                      <strong>CMS:</strong>{" "}
                      {siteInfo.cms?.status === "Detected"
                        ? `${siteInfo.cms.cms} (${siteInfo.cms.confidence})`
                        : "Unknown"}
                    </p>
                    <p>
                      <strong>Powered by:</strong> {siteInfo.powered_by || "Unknown"}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ───────── Security Risk Bar ───────── */}
            {result && (() => {
              const rawPercent = computeRiskPercent();
              let barColorClass = "bg-green-600";
              if (rawPercent >= 85) barColorClass = "bg-red-500";
              else if (rawPercent >= 60) barColorClass = "bg-orange-500";
              else if (rawPercent >= 30) barColorClass = "bg-yellow-500";

              return (
                <div class="max-w-[85rem] mx-auto mb-6">
                  <div class="relative w-full h-4 bg-gray-200 rounded">
                    <div
                      class={`${barColorClass} h-4 rounded`}
                      style={{ width: `${rawPercent}%` }}
                    ></div>
                    <img
                      src="/images/arrow.png"
                      alt="Arrow"
                      class="absolute w-4 h-5"
                      style={{
                        top: "50%",
                        transform: "translate(-50%, -50%)",
                        left: `${rawPercent}%`,
                      }}
                    />
                  </div>
                  <div class="flex justify-between text-sm text-gray-500 mt-2 px-1">
                    <span>Minimal</span>
                    <span>Low</span>
                    <span>Medium</span>
                    <span>High</span>
                    <span>Critical</span>
                  </div>
                </div>
              );
            })()}

            {/* ───────── Risk Explanation Note ───────── */}
            {result && (
              <div class="max-w-[85rem] mx-auto bg-[#0f172a] p-4 rounded shadow text-sm text-white">
                Our automated scan did not detect malware on your site. If you still believe that
                your site has been hacked, <a class="text-[#ff002b]" href="/review">sign up</a> for a
                complete scan, manual audit, and guaranteed malware removal.
              </div>
            )}
          </div>
        )}

        {/* ───── Main Grid: Detailed Malware + Monitoring + Firewall + Blacklist ───── */}
        {result && (
          <div class="max-w-[85rem] mx-auto mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
            {/* ───── LEFT COLUMN ───── */}
            <div class="flex flex-col h-full space-y-4">
              {/* ───── Detailed Malware Section ───── */}
              <div
                class={`bg-[#0f172a] text-[#04c36f] border-2 ${
                  monitorData === null
                    ? "border-gray-300"
                    : (
                        Number(monitorData.uptime?.status) >= 200 &&
                        Number(monitorData.uptime?.status) < 300 &&
                        monitorData.spam?.detected === false &&
                        monitorData.defacement?.detected === false &&
                        monitorData.ssl?.valid === true &&
                        monitorData.spam?.sql_injection === false
                      )
                      ? "border-green-400"
                      : "border-[#ff002b]"
                } p-8 rounded shadow flex-1`}
              >
                <h3 class="text-3xl text-white font-semibold mb-8 flex justify-center">
                  Website Malware & Security
                </h3>

                {!monitorData && (
                  <div class="flex items-center space-x-2 justify-center">
                    <svg
                      class="animate-spin h-6 w-6 text-yellow-200"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      ></circle>
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8z"
                      ></path>
                    </svg>
                    <p class="text-yellow-200 text-sm animate-pulse">Scanning...</p>
                  </div>
                )}

               {/* {monitorData && (
                   <ul class="text-base text-white grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 mb-2"> */}
                    {/* Uptime */}
                    {/* <li class="flex items-start gap-2">
                      <img
                        src={
                          Number(monitorData.uptime?.status) >= 200 &&
                          Number(monitorData.uptime?.status) < 300
                            ? "/images/check_new.png"
                            : "/images/warn_red2.png"
                        }
                        alt={
                          Number(monitorData.uptime?.status) >= 200 &&
                          Number(monitorData.uptime?.status) < 300
                            ? "✅"
                            : "⚠️"
                        }
                        class="w-6 h-6"
                      />
                      <span>
                        Uptime: {monitorData.uptime?.status ?? "Unavailable"}
                      </span>
                    </li> */}

                    {/* Spam */}
                    {/* <li class="flex items-start gap-2">
                      <img
                        src={
                          monitorData.spam?.detected
                            ? "/images/warn_red2.png"
                            : "/images/check_new.png"
                        }
                        alt={monitorData.spam?.detected ? "⚠️" : "✅"}
                        className="w-6 h-6"
                      />
                      <span>
                        {monitorData.spam?.detected
                          ? "Spam indicators found"
                          : "No injected spam detected"}
                      </span>
                    </li> */}

                    {/* Response Time */}
                    {/* <li class="flex items-start gap-2">
                      <img src="/images/check_new.png" alt="✅" class="w-6 h-6" />
                      <span>
                        Response Time:{" "}
                        {monitorData.uptime?.response_time
                          ? `${monitorData.uptime.response_time.toFixed(2)}s`
                          : "Unavailable"}
                      </span>
                    </li> */}

                    {/* Defacement */}
                    {/* <li class="flex items-start gap-2">
                      <img
                        src={
                          monitorData.defacement?.detected
                            ? "/images/warn_red2.png"
                            : "/images/check_new.png"
                        }
                        alt={monitorData.defacement?.detected ? "⚠️" : "✅"}
                        className="w-6 h-6"
                      />
                      <span>
                        {monitorData.defacement?.detected
                          ? "Possible defacement detected"
                          : "No defacement detected"}
                      </span>
                    </li> */}

                    {/* SSL */}
                    {/* <li class="flex items-start gap-2">
                      <img
                        src={monitorData.ssl?.valid ? "/images/check_new.png" : "/images/warn_red2.png"}
                        alt={monitorData.ssl?.valid ? "✅" : "⚠️"}
                        class="w-6 h-6"
                      />
                      <span>
                        SSL:{" "}
                        {monitorData.ssl?.valid
                          ? `Valid – ${monitorData.ssl.days_remaining} days`
                          : "Invalid"}
                      </span>
                    </li> */}

                    {/* SQL Injection */}
                    {/* <li class="flex items-start gap-2">
                      <img
                        src={
                          monitorData.spam?.sql_injection
                            ? "/images/warn_red2.png"
                            : "/images/check_new.png"
                        }
                        alt={monitorData.spam?.sql_injection ? "⚠️" : "✅"}
                        className="w-6 h-6"
                      />
                      <span>
                        SQL Injection:{" "}
                        {monitorData.spam?.sql_injection ? "Suspicious" : "Clean"}
                      </span>
                    </li> */}

                    {/* Request Review button */}
                    {/* <li class="col-span-2 flex justify-center mt-2">
                        <button
                          type="button"
                          // onClick={() => window.location.href = "/review"}
                          onClick={() => setModalOpen(true)}
                          className={`px-4 py-2 rounded text-white ${
                            Number(monitorData.uptime?.status) >= 200 &&
                            Number(monitorData.uptime?.status) < 300 &&
                            monitorData.spam?.detected === false &&
                            monitorData.defacement?.detected === false &&
                            monitorData.ssl?.valid === true &&
                            monitorData.spam?.sql_injection === false
                            ? "bg-green-400 hover:bg-blue-800"
                            : "bg-[#ff002b] hover:bg-red-800"
                          }`}
                        >
                          Request Review
                        </button>
                       
                    </li>
                  </ul>
                )}*/}
                {monitorData && (
                <ul class="text-base text-white grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 mb-2">
                  {/* Uptime */}
                  <li class="flex items-start gap-2">
                    <img
                      src={
                        monitorData.uptime?.status == null
                          ? "/images/ques2.png"
                          : Number(monitorData.uptime.status) >= 200 &&
                            Number(monitorData.uptime.status) < 300
                          ? "/images/check_new.png"
                          : "/images/warn_red2.png"
                      }
                      alt="Uptime"
                      class="w-6 h-6"
                    />
                    <span>
                      Uptime: {monitorData.uptime?.status ?? "Unavailable"}
                    </span>
                  </li>

                  {/* Spam */}
                  <li class="flex items-start gap-2">
                    <img
                      src={
                        monitorData.spam?.detected == null
                          ? "/images/ques2.png"
                          : monitorData.spam.detected
                          ? "/images/warn_red2.png"
                          : "/images/check_new.png"
                      }
                      alt="Spam"
                      class="w-6 h-6"
                    />
                    <span>
                      {monitorData.spam?.detected == null
                        ? "Spam status: unavailable"
                        : monitorData.spam.detected
                        ? "Spam indicators found"
                        : "No injected spam detected"}
                    </span>
                  </li>

                  {/* Response Time */}
                  <li class="flex items-start gap-2">
                    <img
                      src={
                        monitorData.uptime?.response_time == null
                          ? "/images/ques2.png"
                          : "/images/check_new.png"
                      }
                      alt="Response Time"
                      class="w-6 h-6"
                    />
                    <span>
                      Response Time:{" "}
                      {monitorData.uptime?.response_time != null
                        ? `${monitorData.uptime.response_time.toFixed(2)}s`
                        : "Unavailable"}
                    </span>
                  </li>

                  {/* Defacement */}
                  <li class="flex items-start gap-2">
                    <img
                      src={
                        monitorData.defacement?.detected == null
                          ? "/images/ques2.png"
                          : monitorData.defacement.detected
                          ? "/images/warn_red2.png"
                          : "/images/check_new.png"
                      }
                      alt="Defacement"
                      class="w-6 h-6"
                    />
                    <span>
                      {monitorData.defacement?.detected == null
                        ? "Defacement status: unavailable"
                        : monitorData.defacement.detected
                        ? "Possible defacement detected"
                        : "No defacement detected"}
                    </span>
                  </li>

                  {/* SSL */}
                  <li class="flex items-start gap-2">
                    <img
                      src={
                        monitorData.ssl?.valid == null
                          ? "/images/ques2.png"
                          : monitorData.ssl.valid
                          ? "/images/check_new.png"
                          : "/images/warn_red2.png"
                      }
                      alt="SSL"
                      class="w-6 h-6"
                    />
                    <span>
                      SSL:{" "}
                      {monitorData.ssl?.valid == null
                        ? "SSL status: unavailable"
                        : monitorData.ssl.valid
                        ? `Valid – ${monitorData.ssl.days_remaining} days`
                        : "Invalid"}
                    </span>
                  </li>

                  {/* SQL Injection */}
                  <li class="flex items-start gap-2">
                    <img
                      src={
                        monitorData.spam?.sql_injection == null
                          ? "/images/ques2.png"
                          : monitorData.spam.sql_injection
                          ? "/images/warn_red2.png"
                          : "/images/check_new.png"
                      }
                      alt="SQLi"
                      class="w-6 h-6"
                    />
                    <span>
                      SQL Injection status:{" "}
                      {monitorData.spam?.sql_injection == null
                        ? "unavailable"
                        : monitorData.spam.sql_injection
                        ? "Suspicious"
                        : "Clean"}
                    </span>
                  </li>

                  {/* Request Review Button */}
                  <li class="col-span-2 flex justify-center mt-2">
                    <button
                      type="button"
                      onClick={() => setModalOpen(true)}
                      className={`px-4 py-2 rounded text-white ${
                        Number(monitorData.uptime?.status) >= 200 &&
                        Number(monitorData.uptime?.status) < 300 &&
                        monitorData.spam?.detected === false &&
                        monitorData.defacement?.detected === false &&
                        monitorData.ssl?.valid === true &&
                        monitorData.spam?.sql_injection === false
                          ? "bg-green-400 hover:bg-blue-800"
                          : "bg-[#ff002b] hover:bg-red-800"
                      }`}
                    >
                      Request Review
                    </button>
                  </li>
                </ul>
                )}
              </div> 

              {/* ───── Side-by-Side Monitoring + Firewall ───── */}
              <div class="flex flex-1 gap-4">
                {/* 📋 Website Monitoring */}
                <div
                  className={`
                    bg-[#0f172a] text-[#04c36f]
                    border-2 ${
                      monitoringCheck === null
                        ? "border-gray-500"
                        : monitoringCheck.status === "detected"
                          ? "border-green-400"
                          : "border-[#ff002b]"
                    }
                    p-6 rounded shadow text-center flex flex-col items-center justify-center w-1/2
                  `}
                >
                  <div className="text-4xl mb-2">
                    {monitoringCheck === null ? (
                      <svg
                        className="animate-spin h-7 w-7 text-yellow-200 mx-auto"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8z"
                        ></path>
                      </svg>
                    ) : (
                      <img
                        src={
                          monitoringCheck.icon === "check"
                            ? "/images/check_new.png"
                            : "/images/warn_red2.png"
                        }
                        alt={monitoringCheck.icon === "check" ? "✅" : "⚠️"}
                        className="w-7 h-7 mx-auto"
                      />
                    )}
                  </div>

                  <h3 className="font-semibold text-lg mb-1 text-white">Website Monitoring</h3>

                  {monitoringCheck === null ? (
                    <p className="text-yellow-200 mb-1 animate-pulse">Checking…</p>
                  ) : (
                    <p className="text-white mb-1">{monitoringCheck.display_text}</p>
                  )}

                  {monitoringCheck && (
                    <button
                      className={`mt-2 ${
                        monitoringCheck.status === "detected"
                          ? "bg-green-500 hover:bg-green-600"
                          : "bg-[#ff002b] hover:bg-red-800"
                      } text-white px-4 py-2 rounded`}
                      type="button"
                    >
                      View Details
                    </button>
                  )}
                </div>

                {/* 🔥 Website Firewall */}
                <div
                  className={`
                    bg-[#0f172a] text-white
                    border-2 ${
                      firewallResult === null
                        ? "border-gray-500"
                        : firewallResult.firewall_detected
                          ? "border-green-400"
                          : "border-[#ff002b]"
                    }
                    p-6 rounded shadow text-center flex flex-col items-center justify-center w-1/2
                  `}
                >
                  <div className="text-4xl mb-2">
                    {firewallResult === null ? (
                      <svg
                        className="animate-spin h-7 w-7 text-yellow-200"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8z"
                        ></path>
                      </svg>
                    ) : (
                      <img
                        src={
                          firewallResult.firewall_detected
                            ? "/images/check_new.png"
                            : "/images/warn_red2.png"
                        }
                        alt={firewallResult.firewall_detected ? "✅" : "⚠️"}
                        className="w-7 h-7 mx-auto"
                      />
                    )}
                  </div>

                  <h3 className="font-semibold text-lg mb-1 text-white">Website Firewall</h3>


                  {/* {firewallResult === null ? (
                    <p className="text-white mb-1">Loading firewall status…</p>
                  ) : firewallResult.firewall_detected ? (
                    <p className="text-white mb-1">Firewall Detected</p>
                  ) : (
                    <p className="text-white mb-1">No firewall detected</p>
                  )}

                  <div className="mt-2 w-full flex justify-center">
                    {firewallResult === null ? null : firewallResult.firewall_detected ? (
                      <p className="text-sm text-white">
                        {(() => {
                          const meaningfulWafs = firewallResult.details.filter(
                            (item) => !item.toLowerCase().includes("potential waf")
                          );
                          return meaningfulWafs.length > 0
                            ? `(${meaningfulWafs.join(", ")})`
                            : `(${firewallResult.details.join(", ")})`;
                        })()}
                      </p>
                    ) : (
                      <button
                        className="bg-[#ff002b] text-white px-4 py-2 rounded hover:bg-red-800"
                        type="button"
                      >
                        Explore Firewall
                      </button>
                    )} */}
                   
                   {firewallResult === null ? (
                      <p className="text-white mb-1">Loading firewall status…</p>
                    ) : firewallResult.firewall_detected ? (
                      <p className="text-white mb-1">Firewall Detected</p>
                    ) : (
                      <p className="text-white mb-1">No firewall detected</p>
                    )}

                    <div className="mt-2 w-full flex justify-center">
                      {firewallResult === null ? null : firewallResult.firewall_detected ? (
                        (() => {
                          const meaningfulWafs = firewallResult.details.filter(
                            (item) => !item.toLowerCase().includes("potential waf")
                          );
                          return meaningfulWafs.length > 0 ? (
                            <p className="text-sm text-white text-center">
                              ({meaningfulWafs.join(", ")})
                            </p>
                          ) : (
                            <button
                              className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                              type="button"
                            >
                              Explore Firewall
                            </button>
                          );
                        })()
                      ) : (
                        <button
                          className="bg-[#ff002b] text-white px-4 py-2 rounded hover:bg-red-800"
                          type="button"
                        >
                          Explore Firewall
                        </button>
                      )}
                    </div>





                  {/* </div> */}
                </div>
              </div>
            </div>

            {/* ───── RIGHT COLUMN: Website Blacklist Status ───── */}
            {(() => {
              const blacklistedCount = Object.values(result!).filter((entry) => {
                if (entry && typeof entry === "object") {
                  const record = entry as ScanResultItem;
                  return record.status?.toLowerCase().includes("blacklisted");
                }
                return false;
              }).length;
              const totalSources = Object.keys(result!).length;
              const flagged = blacklistedCount > 0;
              const borderClass = flagged ? "border-[#ff002b]" : "border-green-400";

              return (
                <div
                  className={`bg-[#0f172a] border-2 ${borderClass} rounded shadow-md p-6 flex flex-col h-full`}
                >
                  <h2 className="text-3xl font-bold text-white mb-6">Website Blacklist Status</h2>
                  <ul className="text-base space-y-4 text-white">

                    {/* {Object.entries(result!).map(([service, data]) => {
                      let statusType: "clean" | "blacklisted" | "error" = "clean";
                      if (data && typeof data === "object") {
                        const record = data as ScanResultItem;
                        if (record.error) statusType = "error";
                        else if (record.status?.toLowerCase().includes("blacklisted")) statusType = "blacklisted";
                        else if (record.status?.toLowerCase().includes("clean")) statusType = "clean";
                      }
                      const iconSrc = statusType === "blacklisted"
                        ? "/images/warn_red2.png"
                        : "/images/check_new.png";
                      const colorClass = statusType === "blacklisted" ? "text-[#ff002b]" : "text-green-400";

                      const label =
                        statusType === "blacklisted"
                          ? `Domain blacklisted by ${service}`
                          : statusType === "clean"
                            ? `Domain clean by ${service}`
                            : `Error from ${service}`;

                      return (
                        <li key={service} className="flex items-start gap-2">
                          <img src={iconSrc} alt="status icon" className="w-6 h-6" />
                          <span className={colorClass}>{label}</span>
                        </li>
                      );
                    })} */}
                    {Object.entries(result!).map(([service, data]) => {
                      let statusType: "clean" | "blacklisted" | "error" = "clean";
                      if (data && typeof data === "object") {
                        const record = data as ScanResultItem;
                        if (record.error) statusType = "error";
                        else if (record.status?.toLowerCase().includes("blacklisted")) statusType = "blacklisted";
                        else if (record.status?.toLowerCase().includes("clean")) statusType = "clean";
                      }

                      // const isBad = statusType === "blacklisted";  //|| statusType === "error";
                      // const isErr = statusType === "error";
                      // const iconSrc = isErr 
                      // iconSrc = isBad
                      //   ? "/images/warn_red2.png"
                      //   : "/images/check_new.png";
                      
                      const iconSrc =
                      statusType === "error"
                        ? "/images/ques2.png"       
                        : statusType === "blacklisted"
                          ? "/images/warn_red2.png"
                          : "/images/check_new.png";

                      // const colorClass = isBad
                      //   ? "text-[#ff002b]"
                      //   : "text-green-400";
                      const colorClass =
                      statusType === "error" || statusType === "blacklisted"
                        ? "text-[#ff002b]"
                        : "text-green-400";

                      const label =
                        statusType === "blacklisted"
                          ? `Domain blacklisted by ${service}`
                          : statusType === "clean"
                            ? `Domain clean by ${service}`
                            : `Error getting status from ${service}`;

                      return (
                        <li key={service} className="flex items-start gap-2">
                          <img src={iconSrc} alt={statusType} className="w-6 h-6" />
                          <span className={colorClass}>{label}</span>
                        </li>
                      );
                    })}



                  </ul>
                  <div className="mt-8 text-sm text-white">
                    {flagged ? (
                      <>
                        Your site is blacklisted by {blacklistedCount} of {totalSources} sources.&nbsp;
                        <a href="/review" className="text-[#ff002b]">Sign&nbsp;up</a>&nbsp;for a more complete scan, manual audit, and guaranteed blacklist removal.
                      </>
                    ) : (
                      <>
                        Your site does not appear to be blacklisted. If you still see security warnings on your site,&nbsp;
                        <a href="/review" className="text-blue-400">sign&nbsp;up</a>&nbsp;for a more complete scan, manual audit, and guaranteed blacklist removal.
                      </>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* ───────── Hardening Improvements ───────── */}
        {result && (
          <div class="max-w-[85rem] mx-auto mt-10 bg-[#0f172a] p-6 rounded shadow">
            <h3 class="font-bold text-3xl text-white mb-6">Hardening Improvements</h3>

            {!hardeningResult ? (
              <div class="flex items-center space-x-2 text-yellow-200">
                <svg
                  class="animate-spin h-5 w-5 text-yellow-200"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                </svg>
                <p class="text-sm animate-pulse">Scanning for hardening issues…</p>
              </div>
            ) : (
              <div class="space-y-6">
                {/* Protection */}
                <div class="bg-[#0f172a] border-2 border-[#ff002b] p-5 rounded shadow-sm">
                  <h4 class="text-xl font-semibold mb-2 text-white">Protection</h4>
                  <ul class="text-base space-y-1 list-disc list-inside text-white">
                    {!firewallResult?.firewall_detected && (
                      <li>
                        <span class="text-[#ff002b]">No website application firewall detected.</span>{' '}
                        <span class="text-white">Consider installing a WAF.</span>
                      </li>
                    )}
                    {hardeningResult.trace_method?.trace_enabled && (
                      <li>
                        <span class="text-[#ff002b]">TRACE method is enabled — disable it to prevent request echo attacks.</span>
                      </li>
                    )}
                    {hardeningResult.server_banner?.exposed && (
                      <li>
                        <span class="text-[#ff002b]">Server banner exposed:</span>{' '}
                        <span class="text-white">{hardeningResult.server_banner.value}</span>
                      </li>
                    )}
                    {hardeningResult.open_ports?.open_ports && hardeningResult.open_ports.open_ports.length > 0 && (
                      <li>
                        <span class="text-[#ff002b]">
                          Ports{' '}
                          {hardeningResult.open_ports.open_ports
                            .map(({ port, service }) => `${port} (${service})`)
                            .join(', ')}{' '}
                          are open —
                        </span>{' '}
                        <span class="text-white">consider restricting access.</span>
                      </li>
                    )}

                      {/* ────────── NEW SPF CHECKS ────────── */}
                      {hardeningResult.spf_record_count !== undefined && hardeningResult.spf_record_count > 1 && (
                        <li>
                          <span class="text-[#ff002b]">
                            More than one SPF record detected ({hardeningResult.spf_record_count} total).
                          </span>{' '}
                          <span class="text-white">
                            Spam protection may not work correctly. Merge multiple SPF records into one.
                          </span>
                        </li>
                      )}

                     
                      {hardeningResult.spf_record_count === 0 && (
                        <li>
                          <span class="text-[#ff002b]">No SPF record found.</span>{' '}
                          <span class="text-white">
                            Consider adding an SPF TXT record to improve email authentication.
                          </span>
                        </li>
                      )}
                    </ul>
                    
                </div>

                {/* Security Headers */}
                <div class="bg-[#0f172a] border-2 border-[#ff002b] p-5 rounded shadow-sm">
                  <h4 class="text-xl font-semibold mb-2 text-white">Security Headers</h4>
                  <ul class="text-base space-y-2 list-disc list-inside text-white">
                    {hardeningResult.security_headers?.missing_headers?.['X-Frame-Options'] && (
                      <li>
                        <span class="text-[#ff002b]">Missing header for ClickJacking Protection.</span>{' '}
                        <span class="text-white">Use CSP: frame-ancestors 'none'.</span>
                      </li>
                    )}
                    {hardeningResult.security_headers?.missing_headers?.['X-Content-Type-Options'] && (
                      <li>
                        <span class="text-[#ff002b]">Missing X-Content-Type-Options header.</span>
                      </li>
                    )}
                    {hardeningResult.security_headers?.missing_headers?.['Content-Security-Policy'] && (
                      <li>
                        <span class="text-[#ff002b]">Missing Content-Security-Policy directive.</span>{' '}
                        <span class="text-white">Recommended: default-src 'self'.</span>
                      </li>
                    )}
                    {hardeningResult.xss_protection?.status === 'missing_or_incorrect' && (
                      <li>
                        <span class="text-[#ff002b]">X-XSS-Protection header is missing or misconfigured.</span>{' '}
                        <span class="text-white">Recommend: 1; mode=block.</span>
                      </li>
                    )}
                  </ul>
                </div>

                <p class="text-base text-white">
                  We recommend addressing these issues to improve your site's resistance to common web attacks.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ───────── Hacked? Get Help Card ───────── */}
        {result && (
          <div class="max-w-[85rem] mx-auto mt-14 border border-white rounded-xl bg-[#000000] p-8 relative">
            <div class="absolute top-0 left-0 right-0 flex items-center justify-center px-8 transform -translate-y-1/2">
              <h2 class="bg-[#000000] px-2 text-3xl font-bold text-white whitespace-nowrap">
                Our Security Solutions
              </h2>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center pt-4">
{/*               
              {[
                {
                  img: "/images/firewall(1).png",
                  img2: "/images/firewall_ff002b.png",
                  size: "w-11 h-12",
                  title: "Website Firewall",
                  text: "Protect and speed up your site",
                  button: "Protect Your Site",
                },
                {
                  img: "/images/white_monitor_resized.png",
                  img2: "/images/monitor_ff002b.png",
                  size: "w-12 h-12",
                  title: "Website Monitoring",
                  text: "Identify malware and issues on your site",
                  button: "Detect Malware",
                },
                {
                  img: "/images/white_bug(2).png",
                  img2: "/images/bug_ff002b.png",
                  size: "w-12 h-12",
                  title: "Malware Removal",
                  text: "Get rid of malware and clean up a hacked site",
                  button: "Remove Malware",
                },
              ].map(({ img, img2, size, title, text, button }) => (
                <div
                  key={title}
                  class="group bg-[#0f172a] rounded-lg p-6 shadow-md flex flex-col items-center text-center space-y-3 transform transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg hover:shadow-gray-500/20"
                >
                  <div class="text-3xl relative">
                    <img src={img} alt={title} class={size} />
                    <img src={img2} alt={title + " hovered"} class={`${size} hidden group-hover:block absolute top-0 left-0`}/>
                  </div>

                  <h3 class="text-xl text-white font-bold transition-colors duration-200 group-hover:text-[#ff002b]">
                    {title}
                  </h3>
                  <p class="text-sm text-white">{text}</p>
                                    
                  <button
                    type="button"
                    class="text-sm bg-[#004e89] text-white py-2 px-4 rounded-md transition-colors duration-150 group-hover:bg-[#ff002b] hover:!bg-[#c2001a]">
                    {button}
                  </button>

                </div>
              ))} */}

                {[
                  {
                    img: "/images/view(1).png",
                    img2: "/images/view.png",
                    size: "w-6 h-6",
                    title: "Threat Intelligence",
                    text: "AI powered threat detection and analysis",
                    button: "Learn More",
                  },
                  {
                    img: "/images/security.png",
                    img2: "/images/security(1).png",
                    size: "w-6 h-6",
                    title: "Penetration Testing",
                    text: "Identify vulnerabilities and issues on your site",
                    button: "Detect Malware",
                  },
                  {
                    img: "/images/flash(1).png",
                    img2: "/images/flash.png",
                    size: "w-6 h-6",
                    title: "SOC Monitoring",
                    text: "24/7 monitoring and rapid incident response",
                    button: "Remove Malware",
                  },
                ].map(({ img, img2, size, title, text, button }) => (
                  <div
                    key={title}
                    class="group bg-[#0f172a] rounded-xl p-6 shadow-md flex flex-col items-center text-center border border-transparent transition-all duration-200 hover:border-[#ff002b]"
                  >
                    {/* Image inside colored box */}
                    <div class="rounded-lg bg-[#1e293b] p-4 transition-all duration-200">  {/* bg-[#1e293b] group-hover:bg-[#ff002b]*/}
                      <div class="relative w-full flex justify-center items-center">
                        <img src={img} alt={title} class={size + " block group-hover:hidden"} />
                        <img src={img2} alt={title + " hovered"} class={size + " hidden group-hover:block"} />
                      </div>
                    </div>

                    <h3 class="mt-4 text-xl text-white font-bold group-hover:text-[#ff002b] transition-colors duration-200">
                      {title}
                    </h3>
                    <p class="mt-1 text-sm text-white">{text}</p>

                    <button
                      type="button"
                      class="mt-4 text-sm bg-[#004e89] text-white py-2 px-4 rounded-md transition-colors duration-150 group-hover:bg-[#ff002b] hover:!bg-[#c2001a]"
                    >
                      {button}
                    </button>
                  </div>
                ))}


            </div>
          </div>
        )}

        {/* ───────── Footer Re-scan Link ───────── */}
        {result && (
          <div class="max-w-3xl mx-auto mt-14 text-center">
            <p class=" text-gray-500">
              Scanned just now.{" "}
              <span
                class="text-[#ff002b] cursor-pointer underline"
                onClick={() => {
                  setUrl("");
                  setResult(null);
                  setSiteInfo(null);
                  setFirewallResult(null);
                  setMonitorData(null);
                  setHardeningResult(null);
                  setMonitoringCheck(null);
                  setError(null);
                }}
              >
                Re-scan
              </span>{" "}
              to clear the cache.
            </p>
          </div>
        )}

        {/* ───────── Debug Section for Site Info Only ───────── */}
        {siteInfo && !result && (
          <div class="max-w-[85rem] mx-auto bg-[#1E2A5A] rounded-xl shadow-md p-6 space-y-4">
            <h2 class="text-2xl font-bold text-white mb-4">Website Information</h2>
            <div class="max-w-6xl mx-auto border border-gray-300 bg-[#fff] rounded-md px-6 py-5 mb-6">
              <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 text-lg text-gray-800">
                {/* Redirect */}
                <div class="flex items-start gap-2">
                  <div class="text-2xl bg-white rounded-lg p-1 text-orange-500">🔁</div>
                  <div>
                    <p class="font-semibold">Redirects to:</p>
                    <a
                      href={siteInfo.redirect_url || `https://${scannedUrl}`}
                      class="text-blue-600 underline break-words"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {siteInfo.redirect_url || `https://${scannedUrl}`}
                    </a>
                  </div>
                </div>

                {/* IP / CDN / Running on */}
                <div class="space-y-1 text-base">
                  <p><strong>IP address:</strong> {siteInfo.ip_address || "Unknown"}</p>
                  <p><strong>CDN:</strong> {siteInfo.cdn || "Unknown"}</p>
                  <p><strong>Running on:</strong> {siteInfo.running_on || "Unknown"}</p>
                </div>

                {/* CMS / Powered by + “More Details” */}
                <div class="space-y-1 text-base">
                  <p>
                    <strong>CMS:</strong>{" "}
                    {siteInfo.cms?.status === "Detected"
                      ? `${siteInfo.cms.cms} (${siteInfo.cms.confidence})`
                      : "Unknown"}
                  </p>
                  <p><strong>Powered by:</strong> {siteInfo.powered_by || "Unknown"}</p>
                  <p><a href="#" class="text-blue-600 italic underline">More Details</a></p>
                </div>
              </div>
            </div>

            <div class="bg-[#000000] text-white p-4 rounded">
              <h3 class="font-bold mb-2">Debug Information</h3>
              <pre class="text-xs overflow-auto bg-[#222] p-3 rounded text-green-400">
                {JSON.stringify(siteInfo, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>

        
{/* 
          {modalOpen && (
        <div class="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
          <div class="bg-[#0f172a] rounded-lg p-6 w-full max-w-md">
            <h2 class="text-2xl font-semibold text-white mb-4">Request a Review</h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.currentTarget as HTMLFormElement;
                const data = {
                  name:  (form.elements.namedItem("name")  as HTMLInputElement).value,
                  email: (form.elements.namedItem("email") as HTMLInputElement).value,
                  phone: (form.elements.namedItem("phone") as HTMLInputElement).value,
                  company: (form.elements.namedItem("company") as HTMLInputElement).value,
                  note: (form.elements.namedItem("note")   as HTMLTextAreaElement).value,
                };
                console.log("Review data:", data);
                setModalOpen(false);
              }}
            >
               <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">Name</label>
                  <input
                    name="name"
                    type="text"
                    required
                    class="w-full bg-[#1e293b] border border-gray-600 text-white placeholder-gray-400 p-2 rounded focus:outline-none focus:border-blue-500"
                  />
                </div>



              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-300 mb-1">Phone</label>
                <input
                  name="phone"
                  type="tel"
                  required
                  inputmode="numeric"
                  placeholder="(555) 123-4567"
                  class="w-full bg-[#1e293b] border border-gray-600 text-white placeholder-gray-400 p-2 rounded focus:outline-none focus:border-blue-500"
                  onInput={(e) => {
                    const el = e.currentTarget as HTMLInputElement;
                    el.value = formatUSPhone(el.value);
                  }}
                />
              </div>

              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-300 mb-1">Email</label>
               
                <input
                    name="email"
                    type="email"
                    required
                    onInvalid={(e) => {
                      // override the default message
                      e.currentTarget.setCustomValidity("Please enter a valid email address.");
                    }}
                    onInput={(e) => {
                      // clear the custom message once they start typing
                      e.currentTarget.setCustomValidity("");
                    }}
                  class="w-full bg-[#1e293b] border border-gray-600 text-white placeholder-gray-400 p-2 rounded focus:outline-none focus:border-blue-500"
                  />
              </div>

                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">Company</label>
                  <input
                    name="company"
                    type="text"
                    class="w-full bg-[#1e293b] border border-gray-600 text-white placeholder-gray-400 p-2 rounded focus:outline-none focus:border-blue-500"
                  />
                  
                </div>


              <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">Note</label>
                  <textarea
                    name="note"
                    rows={4}
                    class="w-full bg-[#1e293b] border border-gray-600 text-white placeholder-gray-400 p-2 rounded focus:outline-none focus:border-blue-500"
                  />
                </div>

              <div class="flex justify-end space-x-3 pt-4 border-t border-gray-700">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  class="px-4 py-2 border border-gray-600 rounded text-gray-300 hover:bg-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  class="px-4 py-2 bg-[#ff002b] text-white rounded hover:bg-red-800"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )} */}

      {modalOpen && (
  <div
    class="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50"
  >
    <div class="bg-[#0f172a] rounded-lg overflow-hidden w-full max-w-md shadow-xl relative">
      {/* ── Header with cross ── */}
      <div class="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
       <h2 class="text-2xl font-semibold text-white">Request a Review</h2>
       <button
         type="button"
         onClick={() => setModalOpen(false)}
         class="text-gray-400 hover:text-gray-200 text-3xl leading-none"
         aria-label="Close"
       >
         ✕
       </button>
     </div>

      {/* ── Form ── */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const form = e.currentTarget as HTMLFormElement;
          const data = {
            name:    (form.elements.namedItem("name")    as HTMLInputElement).value,
            email:   (form.elements.namedItem("email")   as HTMLInputElement).value,
            phone:   (form.elements.namedItem("phone")   as HTMLInputElement).value,
            company: (form.elements.namedItem("company") as HTMLInputElement).value,
            note:    (form.elements.namedItem("note")    as HTMLTextAreaElement).value,
          };
          console.log("Review data:", data);
          alert("Form submitted!");
          setModalOpen(false);
        }}
        class="px-6 py-4 space-y-4"
      >
        {/* Name */}
        <div>
          <label class="block text-sm font-medium text-gray-300 mb-1">Name</label>
          <input
            name="name"
            type="text"
            required
            class="w-full bg-[#1e293b] border border-gray-600 text-white placeholder-gray-400 p-2 rounded focus:outline-none focus:border-blue-500"
          />
        </div>
        {/* Email */}
        <div>
          <label class="block text-sm font-medium text-gray-300 mb-1">Email</label>
          <input
            name="email"
            type="email"
            required
            onInvalid={e => e.currentTarget.setCustomValidity("Please enter a valid email address.")}
            onInput={e => e.currentTarget.setCustomValidity("")}
            class="w-full bg-[#1e293b] border border-gray-600 text-white placeholder-gray-400 p-2 rounded focus:outline-none focus:border-blue-500"
          />
        </div>
        {/* Phone */}
        <div>
          <label class="block text-sm font-medium text-gray-300 mb-1">Phone</label>
          <input
            name="phone"
            type="tel"
            required
            inputmode="numeric"
            placeholder="(555) 123-4567"
            onInput={e => {
              const el = e.currentTarget as HTMLInputElement;
              const digits = el.value.replace(/\D/g, "").slice(0,10);
              const p1 = digits.slice(0,3), p2 = digits.slice(3,6), p3 = digits.slice(6);
              el.value = p3 ? `(${p1}) ${p2}-${p3}` : p2 ? `(${p1}) ${p2}` : p1 ? `(${p1}` : "";
            }}
            class="w-full bg-[#1e293b] border border-gray-600 text-white placeholder-gray-400 p-2 rounded focus:outline-none focus:border-blue-500"
          />
        </div>
        {/* Company */}
        <div>
          <label class="block text-sm font-medium text-gray-300 mb-1">Company</label>
          <input
            name="company"
            type="text"
            class="w-full bg-[#1e293b] border border-gray-600 text-white placeholder-gray-400 p-2 rounded focus:outline-none focus:border-blue-500"
          />
        </div>
        {/* Note */}
        <div>
          <label class="block text-sm font-medium text-gray-300 mb-1">Note</label>
          <textarea
            name="note"
            rows={4}
            class="w-full bg-[#1e293b] border border-gray-600 text-white placeholder-gray-400 p-2 rounded focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* ── Actions ── */}
        <div class="flex justify-end space-x-3 pt-4 border-t border-gray-700">
          <button
            type="button"
            onClick={(e) => {
              // clear all fields
              const form = (e.currentTarget as HTMLButtonElement).closest("form") as HTMLFormElement;
              form.reset();
            }}
            class="px-4 py-2 border border-gray-600 text-gray-300 rounded hover:bg-gray-800 transition"
          >
            Clear
          </button>
          <button
            type="submit"
            class="px-4 py-2 bg-[#ff002b] text-white rounded hover:bg-red-800 transition"
          >
            Submit
          </button>
        </div>
      </form>
    </div>
  </div>
)}



   
      </>
  );
}